if (!customElements.get('image-compare-slider')) {
  customElements.define(
    'image-compare-slider',
    class ImageCompareSlider extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('.image-compare__range');
        this.handle = this.querySelector('.image-compare__handle');
        if (!this.range || this.hasAttribute('data-initialized')) return;
        this.setAttribute('data-initialized', '');

        this.percent = parseFloat(this.range.value) || 0;
        this.grabOffset = 0;
        this.pendingPercent = null;
        this.frame = null;
        this.isRtl = false;

        this.onInput = this.onInput.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onDragStart = this.onDragStart.bind(this);
        this.flush = this.flush.bind(this);

        this.range.addEventListener('input', this.onInput);
        this.addEventListener('pointerdown', this.onPointerDown);
        this.addEventListener('pointermove', this.onPointerMove);
        this.addEventListener('pointerup', this.onPointerUp);
        this.addEventListener('pointercancel', this.onPointerUp);
        // Belt and braces with preventDefault() on pointerdown: a native image
        // drag steals the pointermove stream and leaves the divider stuck.
        this.addEventListener('dragstart', this.onDragStart);

        this.setPosition(this.range.value);
      }

      disconnectedCallback() {
        this.endDrag();
      }

      onDragStart(event) {
        event.preventDefault();
      }

      onInput() {
        this.setPosition(this.range.value);
      }

      // The divider tracks a float percentage for smooth motion, while the range
      // input — the accessible control — stays on whole numbers for keyboard steps.
      setPosition(value) {
        const percent = Math.min(100, Math.max(0, parseFloat(value) || 0));
        this.percent = percent;
        this.style.setProperty('--image-compare-position', `${percent}%`);
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        // Stops the native image drag and text selection that would otherwise
        // cancel the gesture partway through.
        event.preventDefault();

        this.dragging = true;
        this.isRtl = getComputedStyle(this).direction === 'rtl';
        this.classList.add('image-compare--dragging');

        // Grabbing the button carries the divider from where it was grabbed;
        // pressing anywhere else on the image jumps the divider to the pointer.
        if (this.handle && this.handle.contains(event.target)) {
          const bounds = this.getBoundingClientRect();
          this.grabOffset = this.percentToClientX(this.percent, bounds) - event.clientX;
        } else {
          this.grabOffset = 0;
        }

        this.setPointerCapture(event.pointerId);
        this.range.focus({ preventScroll: true });
        this.updateFromPointer(event);
      }

      onPointerMove(event) {
        if (!this.dragging) return;
        event.preventDefault();
        this.updateFromPointer(event);
      }

      onPointerUp() {
        this.endDrag();
      }

      endDrag() {
        if (this.frame) {
          cancelAnimationFrame(this.frame);
          this.frame = null;
          this.flush();
        }
        this.dragging = false;
        this.grabOffset = 0;
        this.classList.remove('image-compare--dragging');
      }

      percentToClientX(percent, bounds) {
        const ratio = this.isRtl ? 1 - percent / 100 : percent / 100;
        return bounds.left + ratio * bounds.width;
      }

      updateFromPointer(event) {
        const bounds = this.getBoundingClientRect();
        if (!bounds.width) return;

        let ratio = (event.clientX + this.grabOffset - bounds.left) / bounds.width;
        if (this.isRtl) ratio = 1 - ratio;

        this.queuePosition(Math.min(100, Math.max(0, ratio * 100)));
      }

      // Pointer events can outpace the display, so collapse them to one style
      // write per frame instead of thrashing layout mid-gesture.
      queuePosition(percent) {
        this.pendingPercent = percent;
        if (this.frame) return;
        this.frame = requestAnimationFrame(this.flush);
      }

      flush() {
        this.frame = null;
        if (this.pendingPercent === null) return;

        const percent = this.pendingPercent;
        this.pendingPercent = null;
        this.range.value = Math.round(percent);
        this.setPosition(percent);
      }
    }
  );
}
